/**
 * Centralised error mapper for Supabase RPC and query errors.
 *
 * Translates known ERROR_CODE patterns from DB exceptions into user-friendly
 * messages. Falls back to a generic message for unrecognised errors, preventing
 * internal schema or logic details from leaking into the UI.
 */
import { OfflineError } from '@/lib/networkGuard';

const ERROR_CODE_MAP: Record<string, string> = {
  ITEM_LIMIT_REACHED: "You've reached your item limit. Upgrade your plan to add more items.",
  ITEM_RATE_LIMIT: "You're creating items too quickly. Please wait a while and try again.",
  GROUP_LIMIT_REACHED:
    "You've reached your group limit. Upgrade your plan to join or create more groups.",
  OWNER_ONLY: 'This item was returned to the owner. Please submit a new request to the owner.',
  HOLDER_ONLY: 'Only the current holder can lend this item.',
  SELF_LOAN: 'You cannot borrow your own item.',
  ALREADY_ON_LOAN: 'This item is already on loan.',
  NOT_AVAILABLE: 'This item is not currently available.',
  ITEM_NOT_FOUND: 'This item could not be found. It may have been deleted.',
  NOT_GROUP_MEMBER: 'The selected borrower is not a member of a group containing this item.',
  RETURN_TO_OWNER_REQUIRED: 'This item must be returned to the owner before it can be lent again.',
  ITEM_HAS_ACTIVE_LOANS: 'This item has active loans. Return all items before deleting.',
};

const GENERIC_ERROR = 'Something went wrong. Please try again.';

/**
 * Extracts a user-friendly error message from a Supabase error object.
 *
 * Checks `error.details` for `ERROR_CODE:<code>` patterns first, then falls
 * back to known `error.message` substrings, and finally returns a generic
 * message.
 *
 * @param error - The Supabase error object, a standard `Error`, or nullish.
 * @returns A user-facing error string (never empty). Returns a generic fallback
 *   when the error is unrecognised or nullish.
 */
export function mapSupabaseError(
  error: Error | { message?: string; details?: string } | null | undefined,
): string {
  if (!error) return GENERIC_ERROR;

  // Offline detection — check before anything else
  if (error instanceof OfflineError || error?.message === 'OFFLINE') {
    return "You're offline. Check your connection and try again.";
  }

  // Detect fetch-level network failures (browser/Capacitor WebView)
  const networkMsg = (error as { message?: string }).message ?? '';
  if (
    networkMsg.includes('Failed to fetch') ||
    networkMsg.includes('NetworkError') ||
    networkMsg.includes('Network request failed') ||
    networkMsg.includes('The Internet connection appears to be offline')
  ) {
    return "You're offline. Check your connection and try again.";
  }

  // Check structured ERROR_CODE in details
  if (error.details) {
    const codeMatch = error.details.match(/ERROR_CODE:(\w+)/);
    if (codeMatch) {
      const mapped = ERROR_CODE_MAP[codeMatch[1]];
      if (mapped) return mapped;
    }
  }

  // Check known message patterns
  const msg = error.message ?? '';

  if (msg.includes('already registered')) {
    return 'This email is already registered. Please sign in instead.';
  }
  if (msg.includes('no longer pending')) {
    return 'This request has already been handled.';
  }
  if (msg.includes('Already a member')) {
    return "You're already a member of this group.";
  }
  if (msg.includes('Invalid or expired invite code')) {
    return 'This invite link is invalid or has expired.';
  }
  if (msg.includes('maximum capacity')) {
    return 'This group is full and cannot accept new members.';
  }
  if (msg.includes('Rate limit exceeded')) {
    return "You're doing this too quickly. Please wait a while and try again.";
  }
  if (msg.includes('Item limit reached')) {
    return "You've reached your item limit. Upgrade your plan to add more items.";
  }
  if (msg.includes('Group limit reached')) {
    return "You've reached your group limit. Upgrade your plan to join or create more groups.";
  }
  if (msg.includes('Access denied')) {
    return 'You do not have permission to perform this action.';
  }
  if (msg.includes('active loans')) {
    return 'Please return all active loans first.';
  }
  if (msg.includes('idx_loan_requests_one_pending_per_user_item')) {
    return 'You already have a pending request for this item.';
  }

  return GENERIC_ERROR;
}
